import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../../utils/api'
import { BaseWidget, ComparisonIndicator } from './BaseWidget'
import { CheckCircleIcon } from '../Icons'
import FinancialWidgetDesignSelector, { DesignTheme } from './FinancialWidgetDesignSelector'
import { useDashboard } from '../../contexts/DashboardContext'

interface SubmittedWidgetProps {
  className?: string
  designTheme?: DesignTheme
  widgetId?: string
}

interface FinancialData {
  netCommission: number
  count: number
  totalAmount: number
}

interface FinancialMetrics {
  metrics: {
    submitted: FinancialData
  }
  previousYear?: {
    metrics: {
      submitted: FinancialData
    }
  }
}

const SubmittedWidget: React.FC<SubmittedWidgetProps> = ({ className, designTheme = 'minimal', widgetId }) => {
  const { t } = useTranslation()
  const [data, setData] = useState<FinancialData | null>(null)
  const [previousYearData, setPreviousYearData] = useState<FinancialData | null>(null)
  const [loading, setLoading] = useState(true)
  const [isMinimal, setIsMinimal] = useState(false)
  
  const { getWidgetParameter, updateWidgetParameter } = useDashboard()
  const period = getWidgetParameter(widgetId || 'submitted', 'period', 'month-to-date')

  // Detect widget height to determine if we should use minimal display
  useEffect(() => {
    const checkHeight = () => {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'submitted'}"]`)
      if (widgetElement) {
        const height = widgetElement.clientHeight
        setIsMinimal(height <= 230)
      }
    }
    
    checkHeight()
    const timer = setTimeout(checkHeight, 100)
    window.addEventListener('resize', checkHeight)
    const observer = new MutationObserver(checkHeight)
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'submitted'}"]`)
    if (widgetElement) {
      observer.observe(widgetElement, { attributes: true, attributeFilter: ['style'] })
    }
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkHeight)
      observer.disconnect()
    }
  }, [widgetId])


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatAmount = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M$`
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K$`
    }
    return formatCurrency(amount)
  }

  const calculateYoYChange = (current: number, previous: number) => {
    if (previous === 0) return 0
    return ((current - previous) / previous) * 100
  }

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/deals/financial-flags-metrics?period=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const metrics: FinancialMetrics = await response.json()
        setData(metrics.metrics.submitted)
        setPreviousYearData(metrics.previousYear?.metrics.submitted || null)
      }
    } catch (error) {
      console.error('Error fetching submitted data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [period])

  const yoyChange = (previousYearData && data) ? calculateYoYChange(data.netCommission, previousYearData.netCommission) : null

  const actions = (
    <select
      value={period}
      onChange={(e) => updateWidgetParameter(widgetId || 'submitted', 'period', e.target.value)}
      className="text-xs bg-white/20 text-white border border-white/30 rounded px-2 py-1"
    >
      <option value="month-to-date">{t('dashboard.widgets.monthToDate')}</option>
      <option value="year-to-date">{t('dashboard.widgets.yearToDate')}</option>
    </select>
  )

  const footer = yoyChange !== null ? (
    <div className="text-white/90">
      <div className="flex items-center space-x-1.5">
        <span className={`text-xs font-medium ${yoyChange > 0 ? 'text-white' : yoyChange < 0 ? 'text-red-200' : 'text-white/70'}`}>
          {yoyChange > 0 ? '↗' : yoyChange < 0 ? '↘' : ''} {yoyChange > 0 ? '+' : ''}{yoyChange.toFixed(1)}%
        </span>
        <span className="text-xs text-white/70">{t('dashboard.vsLastYear')}</span>
      </div>
    </div>
  ) : null

  return (
    <BaseWidget
      title={t('dashboard.widgets.submitted')}
      icon={<CheckCircleIcon className="h-5 w-5" />}
      accent="analytics"
      variant="gradient"
      className={className}
      loading={loading || !data}
      actions={actions}
      footer={footer}
      minimalDisplay={isMinimal}
    >
      {isMinimal ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-3xl font-bold text-white tabular-nums">
              {formatCurrency(data?.netCommission || 0)}
            </div>
            <div className="text-xs text-white/70 uppercase tracking-wider mt-1">
              {t('dashboard.widgets.submitted')}
            </div>
          </div>
        </div>
      ) : (
        <FinancialWidgetDesignSelector
          data={data}
          title={t('dashboard.widgets.submitted')}
          accent="analytics"
          designTheme={designTheme}
        />
      )}
    </BaseWidget>
  )
}

export default SubmittedWidget

